import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

interface Company {
  id: number;
  name: string;
  company_name?: string;
}

interface SlackIntegration {
  id: number;
  slack_team_id: string;
  slack_channel_id: string | null;
  slack_team_name: string | null;
  company_id: number;
  company_name?: string;
  company_contact_name?: string;
}

const Settings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'user' | 'company' | 'slack'>('user');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<'email' | 'totp' | null>(null);
  const [loading, setLoading] = useState(true);
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showTotpSetupModal, setShowTotpSetupModal] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpQrCode, setTotpQrCode] = useState('');
  const [totpManualKey, setTotpManualKey] = useState('');
  const [totpVerificationCode, setTotpVerificationCode] = useState('');
  const [generatingTotp, setGeneratingTotp] = useState(false);
  const [verifyingTotp, setVerifyingTotp] = useState(false);
  const [slackUserId, setSlackUserId] = useState('');
  const [savingSlackUserId, setSavingSlackUserId] = useState(false);
  const [userName, setUserName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // Availability settings (for agents only)
  const isAgent = !user?.company_id;
  const [quietTimeStart, setQuietTimeStart] = useState('');
  const [quietTimeEnd, setQuietTimeEnd] = useState('');
  const [quietTimezone, setQuietTimezone] = useState('');
  const [outOfOffice, setOutOfOffice] = useState(false);
  const [agentOfLastResort, setAgentOfLastResort] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);

  const isGlobalAdmin = !!(user?.role === 'admin' && !user?.company_id);
  const isCompanyAdmin = !!(user?.role === 'admin' && user?.company_id);
  const [slackIntegrations, setSlackIntegrations] = useState<SlackIntegration[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [slackLoading, setSlackLoading] = useState(false);
  const [showSlackModal, setShowSlackModal] = useState(false);
  const [slackForm, setSlackForm] = useState({ slack_team_id: '', slack_channel_id: '', slack_team_name: '', company_id: '' });
  const [slackSaving, setSlackSaving] = useState(false);

  // SSO State
  const [ssoConfig, setSsoConfig] = useState({
    sso_enabled: false,
    sso_type: 'azure',
    sso_config: {
      tenantId: '',
      clientId: '',
      clientSecret: ''
    }
  });
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoMessage, setSsoMessage] = useState({ type: '', text: '' });

  // Slack auto-post state
  const [slackAutoPost, setSlackAutoPost] = useState(false);
  const [slackAutoPostLoading, setSlackAutoPostLoading] = useState(false);
  const [slackAutoPostMessage, setSlackAutoPostMessage] = useState({ type: '', text: '' });

  // MFA requirement state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaRequiredLoading, setMfaRequiredLoading] = useState(false);
  const [mfaRequiredMessage, setMfaRequiredMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setMfaEnabled(user.mfa_enabled || false);
      setMfaMethod((user as any).mfa_method || 'email');
      setSlackUserId(user.slack_user_id || '');
      setUserName((user as any).name || '');
      setQuietTimeStart((user as any).quiet_time_start || '');
      setQuietTimeEnd((user as any).quiet_time_end || '');
      setQuietTimezone((user as any).quiet_timezone || '');
      setOutOfOffice((user as any).out_of_office || false);
      setAgentOfLastResort((user as any).agent_of_last_resort || false);
      setLoading(false);
      
      if (user.company_id) {
        // Fetch MFA requirement for all company users (to show warning if required)
        fetchMfaRequired(user.company_id);
        
        if (isCompanyAdmin) {
          fetchSsoConfig(user.company_id);
          fetchSlackAutoPost(user.company_id);
        }
      }
    }
  }, [user]);

  const fetchSsoConfig = async (companyId: number) => {
    try {
      const response = await api.get(`/companies/${companyId}/sso`);
      if (response.data) {
        setSsoConfig({
          sso_enabled: !!response.data.sso_enabled,
          sso_type: response.data.sso_type || 'azure',
          sso_config: response.data.sso_config || { tenantId: '', clientId: '', clientSecret: '' }
        });
      }
    } catch (error) {
      console.error('Error fetching SSO config:', error);
    }
  };

  const fetchSlackAutoPost = async (companyId: number) => {
    try {
      const response = await api.get(`/companies/${companyId}/slack-auto-post`);
      if (response.data) {
        setSlackAutoPost(!!response.data.slack_auto_post);
      }
    } catch (error) {
      console.error('Error fetching Slack auto-post setting:', error);
    }
  };

  const fetchMfaRequired = async (companyId: number) => {
    try {
      const response = await api.get(`/companies/${companyId}/mfa-required`);
      if (response.data) {
        setMfaRequired(!!response.data.mfa_required);
      }
    } catch (error) {
      console.error('Error fetching MFA requirement setting:', error);
    }
  };

  const handleMfaRequiredSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.company_id) return;
    
    setMfaRequiredLoading(true);
    setMfaRequiredMessage({ type: '', text: '' });
    try {
      await api.post(`/companies/${user.company_id}/mfa-required`, { mfa_required: mfaRequired });
      setMfaRequiredMessage({ type: 'success', text: 'MFA requirement updated successfully' });
    } catch (error: any) {
      setMfaRequiredMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update MFA requirement' });
    } finally {
      setMfaRequiredLoading(false);
    }
  };

  const handleSlackAutoPostSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.company_id) return;
    
    setSlackAutoPostLoading(true);
    setSlackAutoPostMessage({ type: '', text: '' });
    try {
      await api.post(`/companies/${user.company_id}/slack-auto-post`, { slack_auto_post: slackAutoPost });
      setSlackAutoPostMessage({ type: 'success', text: 'Slack auto-post setting updated successfully' });
    } catch (error: any) {
      setSlackAutoPostMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update Slack auto-post setting' });
    } finally {
      setSlackAutoPostLoading(false);
    }
  };

  const handleSsoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.company_id) return;
    
    setSsoLoading(true);
    setSsoMessage({ type: '', text: '' });
    try {
      await api.post(`/companies/${user.company_id}/sso`, ssoConfig);
      setSsoMessage({ type: 'success', text: 'SSO configuration updated successfully' });
    } catch (error: any) {
      setSsoMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update SSO configuration' });
    } finally {
      setSsoLoading(false);
    }
  };

  useEffect(() => {
    if (!isGlobalAdmin) return;
    const fetchSlack = async () => {
      setSlackLoading(true);
      try {
        const [intRes, compRes] = await Promise.all([
          api.get<SlackIntegration[]>('/slack/integrations'),
          api.get<Company[]>('/companies'),
        ]);
        setSlackIntegrations(intRes.data);
        setCompanies(compRes.data);
      } catch (e) {
        console.error('Fetch Slack integrations / companies:', e);
      } finally {
        setSlackLoading(false);
      }
    };
    fetchSlack();
  }, [isGlobalAdmin]);

  const handleSendVerificationCode = async () => {
    setSendingCode(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/auth/mfa/send-code');
      setSuccess('Verification code sent to your email');
      setShowEnableModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleEnableMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await api.post('/auth/mfa/enable', { code: verificationCode });
      setMfaEnabled(true);
      updateUser({ mfa_enabled: true });
      setShowEnableModal(false);
      setVerificationCode('');
      setSuccess('MFA enabled successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to enable MFA');
    }
  };

  const handleDisableMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await api.post('/auth/mfa/disable', { password });
      setMfaEnabled(false);
      setMfaMethod(null);
      updateUser({ mfa_enabled: false, mfa_method: undefined });
      setShowDisableModal(false);
      setPassword('');
      setSuccess('MFA disabled successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disable MFA');
    }
  };

  const handleGenerateTotp = async () => {
    setGeneratingTotp(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.get('/auth/mfa/totp/generate');
      setTotpSecret(response.data.secret);
      setTotpQrCode(response.data.qrCode);
      setTotpManualKey(response.data.manualEntryKey);
      setShowTotpSetupModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate authenticator setup');
    } finally {
      setGeneratingTotp(false);
    }
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyingTotp(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/auth/mfa/totp/verify', {
        secret: totpSecret,
        code: totpVerificationCode
      });
      setMfaEnabled(true);
      setMfaMethod('totp');
      updateUser({ mfa_enabled: true, mfa_method: 'totp' });
      setShowTotpSetupModal(false);
      setTotpSecret('');
      setTotpQrCode('');
      setTotpManualKey('');
      setTotpVerificationCode('');
      setSuccess('Authenticator app MFA enabled successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to verify authenticator code');
    } finally {
      setVerifyingTotp(false);
    }
  };

  const handleSaveSlackUserId = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSavingSlackUserId(true);
    try {
      const response = await api.put('/auth/me/slack', { slack_user_id: slackUserId.trim() || null });
      updateUser({ slack_user_id: response.data.user.slack_user_id });
      setSuccess('Slack User ID updated successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update Slack User ID');
    } finally {
      setSavingSlackUserId(false);
    }
  };

  const handleUpdateName = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!userName.trim()) {
      setError('Full name is required');
      return;
    }
    setSavingName(true);
    try {
      const response = await api.put('/auth/me', { name: userName.trim() });
      updateUser({ name: response.data.user.name });
      setSuccess('Full name updated successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.errors?.map((e: any) => e.msg).join(', ') || 'Failed to update full name');
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await api.put('/auth/me/password', {
        currentPassword,
        newPassword
      });
      setSuccess('Password changed successfully');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.errors?.map((e: any) => e.msg).join(', ') || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveAvailability = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSavingAvailability(true);
    try {
      const response = await api.put('/auth/me/availability', {
        quiet_time_start: quietTimeStart || null,
        quiet_time_end: quietTimeEnd || null,
        quiet_timezone: quietTimezone || null,
        out_of_office: outOfOffice,
        agent_of_last_resort: agentOfLastResort
      });
      updateUser({
        quiet_time_start: response.data.user.quiet_time_start,
        quiet_time_end: response.data.user.quiet_time_end,
        quiet_timezone: response.data.user.quiet_timezone,
        out_of_office: response.data.user.out_of_office,
        agent_of_last_resort: response.data.user.agent_of_last_resort
      });
      setSuccess('Availability settings updated successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.errors?.map((e: any) => e.msg).join(', ') || 'Failed to update availability settings');
    } finally {
      setSavingAvailability(false);
    }
  };

  const handleAddSlackIntegration = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSlackSaving(true);
    try {
      if (!slackForm.slack_channel_id.trim()) {
        setError('Slack Channel ID is required');
        setSlackSaving(false);
        return;
      }
      
      await api.post('/slack/integrations', {
        slack_team_id: slackForm.slack_team_id.trim(),
        slack_channel_id: slackForm.slack_channel_id.trim(),
        slack_team_name: slackForm.slack_team_name.trim() || undefined,
        company_id: parseInt(slackForm.company_id, 10),
      });
      setShowSlackModal(false);
      setSlackForm({ slack_team_id: '', slack_channel_id: '', slack_team_name: '', company_id: '' });
      setSuccess('Slack integration added');
      const intRes = await api.get<SlackIntegration[]>('/slack/integrations');
      setSlackIntegrations(intRes.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add Slack integration');
    } finally {
      setSlackSaving(false);
    }
  };

  const handleDeleteSlackIntegration = async (id: number) => {
    if (!window.confirm('Remove this Slack integration?')) return;
    setError('');
    setSuccess('');
    try {
      await api.delete(`/slack/integrations/${id}`);
      setSuccess('Slack integration removed');
      setSlackIntegrations((prev) => prev.filter((s) => s.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove integration');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">Settings</h1>

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('user')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'user'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            User Settings
          </button>
          {isCompanyAdmin && (
            <button
              onClick={() => setActiveTab('company')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'company'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Company Settings
            </button>
          )}
          {isGlobalAdmin && (
            <button
              onClick={() => setActiveTab('slack')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'slack'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Slack Integrations
            </button>
          )}
        </nav>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {activeTab === 'user' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Update Full Name</h2>
              <form onSubmit={handleUpdateName} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="John Doe"
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={savingName}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {savingName ? 'Saving...' : 'Update Full Name'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Change Password</h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Update your password to keep your account secure.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Multi-Factor Authentication</h2>
              
              {mfaRequired && !mfaEnabled && user?.company_id && (
                <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        MFA Required by Your Company
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                        <p>
                          Your company requires Multi-Factor Authentication. You must enable MFA to continue using your account. 
                          You will not be able to log in until MFA is enabled.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-4">
                {mfaEnabled ? (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          MFA is currently enabled using {mfaMethod === 'totp' ? 'Authenticator App' : 'Email'}.
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          You will need to enter a verification code when logging in.
                        </p>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => setShowDisableModal(true)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Disable MFA
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          MFA is currently disabled. Enable it to add an extra layer of security to your account.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={handleSendVerificationCode}
                        disabled={sendingCode}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {sendingCode ? 'Sending...' : 'Enable Email MFA'}
                      </button>
                      <button
                        onClick={handleGenerateTotp}
                        disabled={generatingTotp}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {generatingTotp ? 'Generating...' : 'Use Authenticator App'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Slack Integration</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Link your Slack account to have your Slack thread replies automatically attributed to you in NxtPortal tickets.
              </p>
              
              <form onSubmit={handleSaveSlackUserId} className="space-y-4">
                <div>
                  <label htmlFor="slack_user_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Slack User ID
                  </label>
                  <input
                    type="text"
                    id="slack_user_id"
                    value={slackUserId}
                    onChange={(e) => setSlackUserId(e.target.value)}
                    placeholder="e.g. U01234567"
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Find your Slack User ID: Right-click on your profile in Slack and select "Copy member ID", or open Slack in a browser, go to your profile, and the User ID is in the URL (starts with 'U').
                  </p>
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={savingSlackUserId}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {savingSlackUserId ? 'Saving...' : 'Save Slack User ID'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {isAgent && (
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Availability Settings</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Configure when you should not receive outage notifications via SMS or phone calls.
                </p>
                
                <form onSubmit={handleSaveAvailability} className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        id="out_of_office"
                        type="checkbox"
                        checked={outOfOffice}
                        onChange={(e) => setOutOfOffice(e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="out_of_office" className="ml-2 block text-sm font-medium text-gray-900 dark:text-gray-100">
                        Out of Office
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                      When enabled, you will not receive any outage notifications.
                    </p>
                    
                    <div className="flex items-center">
                      <input
                        id="agent_of_last_resort"
                        type="checkbox"
                        checked={agentOfLastResort}
                        onChange={(e) => setAgentOfLastResort(e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="agent_of_last_resort" className="ml-2 block text-sm font-medium text-gray-900 dark:text-gray-100">
                        Agent of Last Resort
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                      When enabled, you will be contacted if all other agents fail to acknowledge, regardless of quiet time or out of office settings.
                    </p>
                  </div>

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Quiet Time</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Set a time range when you should not receive outage notifications. Leave empty to disable.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label htmlFor="quiet_time_start" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Start Time
                        </label>
                        <input
                          type="time"
                          id="quiet_time_start"
                          value={quietTimeStart}
                          onChange={(e) => setQuietTimeStart(e.target.value)}
                          className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="quiet_time_end" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          End Time
                        </label>
                        <input
                          type="time"
                          id="quiet_time_end"
                          value={quietTimeEnd}
                          onChange={(e) => setQuietTimeEnd(e.target.value)}
                          className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="quiet_timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Time Zone
                        </label>
                        <select
                          id="quiet_timezone"
                          value={quietTimezone}
                          onChange={(e) => setQuietTimezone(e.target.value)}
                          className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                          <option value="">Select timezone...</option>
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="America/Anchorage">Alaska Time (AKT)</option>
                          <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
                          <option value="UTC">UTC</option>
                          <option value="Europe/London">London (GMT/BST)</option>
                          <option value="Europe/Paris">Paris (CET/CEST)</option>
                          <option value="Europe/Berlin">Berlin (CET/CEST)</option>
                          <option value="Asia/Tokyo">Tokyo (JST)</option>
                          <option value="Asia/Shanghai">Shanghai (CST)</option>
                          <option value="Asia/Dubai">Dubai (GST)</option>
                          <option value="Australia/Sydney">Sydney (AEDT/AEST)</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Quiet time can span midnight (e.g., 22:00 to 08:00).
                    </p>
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={savingAvailability}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {savingAvailability ? 'Saving...' : 'Save Availability Settings'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'company' && isCompanyAdmin && (
        <div className="max-w-2xl">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Single Sign-On Configuration</h2>
          
          <form onSubmit={handleSsoSubmit} className="space-y-6 bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6">
            {ssoMessage.text && (
              <div className={`p-4 rounded-md ${ssoMessage.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'}`}>
                {ssoMessage.text}
              </div>
            )}

            <div className="flex items-center">
              <input
                id="sso_enabled"
                type="checkbox"
                checked={ssoConfig.sso_enabled}
                onChange={(e) => setSsoConfig({ ...ssoConfig, sso_enabled: e.target.checked })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="sso_enabled" className="ml-2 block text-sm font-medium text-gray-900 dark:text-gray-100">
                Enable SSO for your company
              </label>
            </div>

            {ssoConfig.sso_enabled && (
              <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">SSO Provider</label>
                    <select
                      value={ssoConfig.sso_type}
                      onChange={(e) => setSsoConfig({ ...ssoConfig, sso_type: e.target.value })}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="azure">Microsoft Azure AD (Entra ID)</option>
                      <option value="google">Google Workspace</option>
                    </select>
                  </div>

                  {ssoConfig.sso_type === 'azure' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tenant ID</label>
                        <input
                          type="text"
                          value={ssoConfig.sso_config.tenantId}
                          onChange={(e) => setSsoConfig({
                            ...ssoConfig,
                            sso_config: { ...ssoConfig.sso_config, tenantId: e.target.value }
                          })}
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="e.g. 00000000-0000-0000-0000-000000000000"
                          required={ssoConfig.sso_enabled && ssoConfig.sso_type === 'azure'}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client ID</label>
                        <input
                          type="text"
                          value={ssoConfig.sso_config.clientId}
                          onChange={(e) => setSsoConfig({
                            ...ssoConfig,
                            sso_config: { ...ssoConfig.sso_config, clientId: e.target.value }
                          })}
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Application (client) ID"
                          required={ssoConfig.sso_enabled}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client Secret</label>
                        <input
                          type="password"
                          value={ssoConfig.sso_config.clientSecret}
                          onChange={(e) => setSsoConfig({
                            ...ssoConfig,
                            sso_config: { ...ssoConfig.sso_config, clientSecret: e.target.value }
                          })}
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Client secret value"
                          required={ssoConfig.sso_enabled}
                        />
                      </div>

                      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-md">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Instructions:</h4>
                        <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-decimal ml-4">
                          <li>Register an application in Azure Portal (App Registrations).</li>
                          <li>Set the Redirect URI to: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{window.location.origin}/api/auth/sso/callback</code></li>
                          <li>Ensure "ID tokens" are enabled in the Authentication settings.</li>
                          <li>Create a client secret and copy its value here.</li>
                        </ol>
                      </div>
                    </div>
                  )}

                  {ssoConfig.sso_type === 'google' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client ID</label>
                        <input
                          type="text"
                          value={ssoConfig.sso_config.clientId}
                          onChange={(e) => setSsoConfig({
                            ...ssoConfig,
                            sso_config: { ...ssoConfig.sso_config, clientId: e.target.value }
                          })}
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Google OAuth 2.0 Client ID"
                          required={ssoConfig.sso_enabled}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client Secret</label>
                        <input
                          type="password"
                          value={ssoConfig.sso_config.clientSecret}
                          onChange={(e) => setSsoConfig({
                            ...ssoConfig,
                            sso_config: { ...ssoConfig.sso_config, clientSecret: e.target.value }
                          })}
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Google OAuth 2.0 Client Secret"
                          required={ssoConfig.sso_enabled}
                        />
                      </div>

                      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-md">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Instructions:</h4>
                      <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-decimal ml-4">
                        <li>Go to the Google Cloud Console and select your project.</li>
                        <li>Go to <strong>APIs & Services</strong> &gt; <strong>Credentials</strong>.</li>
                        <li>Click <strong>Create Credentials</strong> &gt; <strong>OAuth client ID</strong>.</li>
                        <li>Select <strong>Web application</strong> as the application type.</li>
                        <li>Set the Authorized redirect URI to: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{window.location.origin}/api/auth/sso/callback</code></li>
                        <li>Copy the Client ID and Client Secret here.</li>
                      </ol>
                      </div>
                    </div>
                  )}
              </div>
            )}

            <div className="pt-5">
              <button
                type="submit"
                disabled={ssoLoading}
                className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {ssoLoading ? 'Saving...' : 'Save SSO Configuration'}
              </button>
            </div>
          </form>

          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Multi-Factor Authentication Requirement</h2>
            
            <form onSubmit={handleMfaRequiredSubmit} className="space-y-6 bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6">
              {mfaRequiredMessage.text && (
                <div className={`p-4 rounded-md ${mfaRequiredMessage.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'}`}>
                  {mfaRequiredMessage.text}
                </div>
              )}

              <div className="flex items-start">
                <input
                  id="mfa_required"
                  type="checkbox"
                  checked={mfaRequired}
                  onChange={(e) => setMfaRequired(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mt-1"
                />
                <div className="ml-3">
                  <label htmlFor="mfa_required" className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                    Require Multi-Factor Authentication for all users
                  </label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    When enabled, all users in your company must configure MFA before they can log in. Users who haven't set up MFA will be blocked from logging in until they enable it.
                  </p>
                </div>
              </div>

              <div className="pt-5">
                <button
                  type="submit"
                  disabled={mfaRequiredLoading}
                  className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {mfaRequiredLoading ? 'Saving...' : 'Save MFA Requirement'}
                </button>
              </div>
            </form>
          </div>

          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Slack Auto-Post Configuration</h2>
            
            <form onSubmit={handleSlackAutoPostSubmit} className="space-y-6 bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6">
              {slackAutoPostMessage.text && (
                <div className={`p-4 rounded-md ${slackAutoPostMessage.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'}`}>
                  {slackAutoPostMessage.text}
                </div>
              )}

              <div className="flex items-center">
                <input
                  id="slack_auto_post"
                  type="checkbox"
                  checked={slackAutoPost}
                  onChange={(e) => setSlackAutoPost(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="slack_auto_post" className="ml-2 block text-sm font-medium text-gray-900 dark:text-gray-100">
                  Automatically post tickets to Slack channel
                </label>
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p className="mb-2">When enabled, every ticket created via the portal will automatically create a corresponding thread in your company&apos;s Slack channel.</p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  <strong>Note:</strong> This requires a Slack integration to be configured for your company. Contact a global admin to set up the integration.
                </p>
              </div>

              <div className="pt-5">
                <button
                  type="submit"
                  disabled={slackAutoPostLoading}
                  className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {slackAutoPostLoading ? 'Saving...' : 'Save Slack Auto-Post Setting'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'slack' && isGlobalAdmin && (
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Slack integration</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Map Slack workspaces to NxtPortal companies so users can create and update tickets via <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/nxtportal</code> slash commands.
            </p>
            {slackLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Workspace → Company</span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSlackModal(true);
                      setError('');
                      setSuccess('');
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                  >
                    Add integration
                  </button>
                </div>
                {slackIntegrations.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No Slack integrations yet. Add one to enable <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/support create</code> and <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/support update</code> in your company&apos;s Slack channel.</p>
                ) : (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {slackIntegrations.map((s) => (
                      <li key={s.id} className="py-3 flex items-center justify-between">
                        <div>
                          <span className="font-mono text-sm text-gray-700 dark:text-gray-300">{s.slack_team_id}</span>
                          {s.slack_channel_id && <span className="ml-2 font-mono text-xs text-gray-600 dark:text-gray-400">#{s.slack_channel_id}</span>}
                          {s.slack_team_name && <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">({s.slack_team_name})</span>}
                          <span className="mx-2 text-gray-400">→</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{s.company_name || s.company_contact_name || `Company #${s.company_id}`}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteSlackIntegration(s.id)}
                          className="text-sm text-red-600 dark:text-red-400 hover:underline"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Slack integration modal */}
      {showSlackModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => setShowSlackModal(false)} />
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleAddSlackIntegration}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">Add Slack integration</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Slack Team ID</label>
                      <input
                        type="text"
                        required
                        placeholder="T01234567"
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                        value={slackForm.slack_team_id}
                        onChange={(e) => setSlackForm((f) => ({ ...f, slack_team_id: e.target.value }))}
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Find it in Slack: Open Slack in a browser, the Team ID is in the URL (e.g., https://app.slack.com/client/T0123...) starting with &apos;T&apos;.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Slack Channel ID <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        required
                        placeholder="C01234567"
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                        value={slackForm.slack_channel_id}
                        onChange={(e) => setSlackForm((f) => ({ ...f, slack_channel_id: e.target.value }))}
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">The unique channel ID for this company&apos;s support channel. Required - each Slack integration must be configured for a specific channel. Find it by right-clicking the channel in Slack and selecting &quot;View channel details&quot; or checking the channel URL.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Workspace name (optional)</label>
                      <input
                        type="text"
                        placeholder="My Workspace"
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                        value={slackForm.slack_team_name}
                        onChange={(e) => setSlackForm((f) => ({ ...f, slack_team_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company</label>
                      <select
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                        value={slackForm.company_id}
                        onChange={(e) => setSlackForm((f) => ({ ...f, company_id: e.target.value }))}
                      >
                        <option value="">Select company</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>{c.company_name || c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={slackSaving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {slackSaving ? 'Saving...' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSlackModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Enable MFA Modal */}
      {showEnableModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => setShowEnableModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEnableMfa}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Enable Multi-Factor Authentication
                  </h3>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      A verification code has been sent to your email. Enter the code below to enable MFA.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Verification Code</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-center text-2xl tracking-widest"
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-base font-medium text-white hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Enable MFA
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEnableModal(false);
                      setVerificationCode('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Disable MFA Modal */}
      {showDisableModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => setShowDisableModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleDisableMfa}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Disable Multi-Factor Authentication
                  </h3>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Please enter your password to confirm you want to disable MFA.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                      <input
                        type="password"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 dark:bg-red-500 text-base font-medium text-white hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Disable MFA
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDisableModal(false);
                      setPassword('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* TOTP Setup Modal */}
      {showTotpSetupModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => setShowTotpSetupModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleVerifyTotp}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Set Up Authenticator App
                  </h3>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.):
                    </p>
                    {totpQrCode && (
                      <div className="flex justify-center">
                        <img src={totpQrCode} alt="QR Code" className="border border-gray-300 dark:border-gray-600 rounded p-2 bg-white" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Or enter this key manually:
                      </p>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded border border-gray-300 dark:border-gray-600">
                        <code className="text-sm font-mono text-gray-900 dark:text-gray-100 break-all">{totpManualKey}</code>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Enter the 6-digit code from your authenticator app
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        required
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-center text-2xl tracking-widest"
                        placeholder="000000"
                        value={totpVerificationCode}
                        onChange={(e) => setTotpVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={verifyingTotp}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-base font-medium text-white hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {verifyingTotp ? 'Verifying...' : 'Enable Authenticator App'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowTotpSetupModal(false);
                      setTotpSecret('');
                      setTotpQrCode('');
                      setTotpManualKey('');
                      setTotpVerificationCode('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => {
                setShowPasswordModal(false);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setError('');
              }}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleChangePassword}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Change Password
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Current Password</label>
                      <input
                        type="password"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New Password</label>
                      <input
                        type="password"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Minimum 6 characters"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm New Password</label>
                      <input
                        type="password"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-base font-medium text-white hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {changingPassword ? 'Changing...' : 'Change Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setError('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
